import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Plus,
  Trash2,
  Newspaper,
  User,
  Loader2,
  AlertCircle,
  Shield,
  ShieldOff,
  Database,
  Building2
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SourceMasterPage = () => {
  const { token, isMainUser } = useAuth();
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('NEWSPAPER');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  
  const [newSource, setNewSource] = useState({
    name: '',
    code: '',
    source_type: 'NEWSPAPER',
    description: ''
  });

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    if (!isMainUser) {
      navigate('/dashboard');
      return;
    }
    fetchSources();
  }, [isMainUser, navigate]);

  const fetchSources = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/source-master`, axiosConfig);
      setSources(response.data);
    } catch (error) {
      toast.error('Failed to fetch source masters');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');

    if (!newSource.name.trim() || !newSource.code.trim()) {
      setError('Name and Code are required');
      return;
    }

    if (newSource.code.length < 2 || newSource.code.length > 10) {
      setError('Code must be 2-10 characters');
      return;
    }

    if (!/^[A-Za-z]+$/.test(newSource.code)) {
      setError('Code must contain only letters');
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/source-master`, {
        ...newSource,
        code: newSource.code.toUpperCase()
      }, axiosConfig);
      toast.success('Source master created successfully');
      setDialogOpen(false);
      setNewSource({ name: '', code: '', source_type: 'NEWSPAPER', description: '' });
      fetchSources();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create source master');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleStatus = async (sourceId) => {
    try {
      await axios.patch(`${API_URL}/api/source-master/${sourceId}/status`, {}, axiosConfig);
      toast.success('Source status updated');
      fetchSources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async (sourceId) => {
    if (!window.confirm('Are you sure you want to delete this source? This will not affect existing records.')) return;

    try {
      await axios.delete(`${API_URL}/api/source-master/${sourceId}`, axiosConfig);
      toast.success('Source master deleted');
      fetchSources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete source master');
    }
  };

  const getSourceTypeBadge = (type) => {
    if (type === 'NEWSPAPER') {
      return <Badge className="bg-purple-100 text-purple-800 border-purple-200"><Newspaper className="h-3 w-3 mr-1" />Newspaper</Badge>;
    } else if (type === 'CUSTOM_PROVIDER') {
      return <Badge className="bg-amber-100 text-amber-800 border-amber-200"><Building2 className="h-3 w-3 mr-1" />Custom</Badge>;
    }
    return <Badge className="bg-teal-100 text-teal-800 border-teal-200"><User className="h-3 w-3 mr-1" />Owner Ref</Badge>;
  };

  const getStatusBadge = (status) => {
    return status === 'ACTIVE'
      ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Active</Badge>
      : <Badge className="bg-red-100 text-red-800 border-red-200">Inactive</Badge>;
  };

  const filteredSources = sources.filter(s => s.source_type === activeTab);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="source-master-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold">Source Master</h1>
            <p className="text-muted-foreground">Manage dropdown options for Newspaper, Owner Reference, and Custom Provider sources</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-source-button" className="gap-2">
                <Plus className="h-4 w-4" />
                Add Source
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Source</DialogTitle>
                <DialogDescription>
                  Create a new source option that users can select during bulk submission.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                    <AlertCircle className="h-4 w-4" />
                    {error}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="source-type">Source Type</Label>
                  <Select 
                    value={newSource.source_type} 
                    onValueChange={(val) => setNewSource(prev => ({ ...prev, source_type: val }))}
                  >
                    <SelectTrigger data-testid="new-source-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NEWSPAPER">
                        <div className="flex items-center gap-2">
                          <Newspaper className="h-4 w-4" />
                          Newspaper
                        </div>
                      </SelectItem>
                      <SelectItem value="OWNER_REFERENCE">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Owner Reference
                        </div>
                      </SelectItem>
                      <SelectItem value="CUSTOM_PROVIDER">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          Custom Provider
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="source-name">Display Name</Label>
                  <Input
                    id="source-name"
                    placeholder="e.g., Deccan Herald, Times of India"
                    value={newSource.name}
                    onChange={(e) => setNewSource(prev => ({ ...prev, name: e.target.value }))}
                    required
                    data-testid="new-source-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="source-code">Code (2-10 letters)</Label>
                  <Input
                    id="source-code"
                    placeholder="e.g., DH, TOI, HT"
                    value={newSource.code}
                    onChange={(e) => setNewSource(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    maxLength={10}
                    required
                    data-testid="new-source-code-input"
                    className="font-mono uppercase"
                  />
                  <p className="text-xs text-muted-foreground">This code will appear in generated Source IDs</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="source-desc">Description (optional)</Label>
                  <Textarea
                    id="source-desc"
                    placeholder="Additional details about this source..."
                    value={newSource.description}
                    onChange={(e) => setNewSource(prev => ({ ...prev, description: e.target.value }))}
                    data-testid="new-source-description-input"
                    rows={2}
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={creating} data-testid="create-source-button">
                    {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Source'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="border-border">
          <CardHeader>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full max-w-lg grid-cols-3">
                <TabsTrigger value="NEWSPAPER" className="gap-2" data-testid="tab-newspaper">
                  <Newspaper className="h-4 w-4" />
                  Newspaper ({sources.filter(s => s.source_type === 'NEWSPAPER').length})
                </TabsTrigger>
                <TabsTrigger value="OWNER_REFERENCE" className="gap-2" data-testid="tab-owner-ref">
                  <User className="h-4 w-4" />
                  Owner ({sources.filter(s => s.source_type === 'OWNER_REFERENCE').length})
                </TabsTrigger>
                <TabsTrigger value="CUSTOM_PROVIDER" className="gap-2" data-testid="tab-custom">
                  <Building2 className="h-4 w-4" />
                  Custom ({sources.filter(s => s.source_type === 'CUSTOM_PROVIDER').length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {filteredSources.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No {activeTab === 'NEWSPAPER' ? 'newspaper' : activeTab === 'CUSTOM_PROVIDER' ? 'custom provider' : 'owner reference'} sources yet.</p>
                <p className="text-sm">Click "Add Source" to create one.</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="w-32">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSources.map((source) => (
                      <motion.tr
                        key={source.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="border-b hover:bg-muted/30"
                      >
                        <TableCell className="font-mono font-medium">{source.code}</TableCell>
                        <TableCell className="font-medium">{source.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                          {source.description || '-'}
                        </TableCell>
                        <TableCell>{getStatusBadge(source.status)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(source.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleStatus(source.id)}
                              data-testid={`toggle-status-${source.id}`}
                              title={source.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                            >
                              {source.status === 'ACTIVE' ? (
                                <ShieldOff className="h-4 w-4 text-amber-600" />
                              ) : (
                                <Shield className="h-4 w-4 text-emerald-600" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(source.id)}
                              data-testid={`delete-source-${source.id}`}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </motion.tr>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Card */}
        <Card className="border-border bg-muted/30">
          <CardContent className="pt-6">
            <h3 className="font-medium mb-2">How Source Master Works</h3>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Sources you add here appear in the dropdown during bulk submission</li>
              <li>The <span className="font-mono bg-background px-1 rounded">CODE</span> is used in generated Source IDs (e.g., SRC-NEWS-<span className="font-mono">TOI</span>-20260203-ABCD12)</li>
              <li>Deactivated sources won't appear in the dropdown but existing records are unaffected</li>
              <li>Deleting a source removes it from the dropdown; existing records remain intact</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default SourceMasterPage;
